import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { adminService } from "../../services/admin.service";

// Check if admin cookie exists
const hasAdminCookie = () => {
  if (typeof document === "undefined") return false;

  // Get all cookies
  const cookies = document.cookie;

  // Check for cookies with more robust matching
  const hasAccessToken = cookies.includes("admin_access_token=");
  const hasRefreshToken = cookies.includes("admin_refresh_token=");

  return hasAccessToken && hasRefreshToken;
};

// Get user from localStorage
const getUserFromStorage = () => {
  try {
    const userStr = localStorage.getItem("admin_user");
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    return null;
  }
};

// Async thunk for login
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await adminService.login(credentials);

      if (response.success) {
        // Extract data from API response
        const { admin, session } = response.data;

        // Save to localStorage
        localStorage.setItem("admin_user", JSON.stringify(admin));
        localStorage.setItem("admin_session", JSON.stringify(session));

        const hasCookies = hasAdminCookie();
        if (!hasCookies) {
          console.warn("Cookies not immediately found, but login successful");
        }

        return {
          user: admin,
          session: session,
          permissions: admin.permissions || [],
        };
      } else {
        // Handle specific API error messages
        let errorMessage = response.message || "Login failed";

        // Map API error messages to user-friendly messages
        if (response.error === "Invalid credentials - Email not found") {
          errorMessage = "البريد الإلكتروني غير مسجل في النظام";
        } else if (response.error === "Invalid credentials - Wrong password") {
          errorMessage = "كلمة المرور غير صحيحة";
        } else if (response.error === "Invalid credentials") {
          errorMessage = "البريد الإلكتروني أو كلمة المرور غير صحيحة";
        }

        return rejectWithValue(errorMessage);
      }
    } catch (error) {
      let errorMessage = "حدث خطأ أثناء تسجيل الدخول";

      if (error.response?.data?.error) {
        const apiError = error.response.data.error;
        if (apiError === "Invalid credentials - Email not found") {
          errorMessage = "البريد الإلكتروني غير مسجل في النظام";
        } else if (apiError === "Invalid credentials - Wrong password") {
          errorMessage = "كلمة المرور غير صحيحة";
        } else if (apiError === "Invalid credentials") {
          errorMessage = "البريد الإلكتروني أو كلمة المرور غير صحيحة";
        } else {
          errorMessage = error.response.data.message || apiError;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for logout
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await adminService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local storage
      localStorage.removeItem("admin_user");
      localStorage.removeItem("admin_session");

      // Clear cookies
      const domain = window.location.hostname;
      document.cookie = `admin_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}`;
      document.cookie = `admin_refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}`;
    }
  },
);

// Async thunk for checking auth status
export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const user = getUserFromStorage();

      if (!user) {
        return rejectWithValue("No user data found");
      }

      // Check if we have admin cookies
      if (!hasAdminCookie()) {
        return rejectWithValue("No authentication cookies found");
      }

      try {
        // Verify session by fetching profile
        const response = await adminService.getProfile();

        if (response.success) {
          // Update user data
          const updatedUser = response.data?.admin || user;
          localStorage.setItem("admin_user", JSON.stringify(updatedUser));

          return {
            user: updatedUser,
            session: JSON.parse(
              localStorage.getItem("admin_session") || "null",
            ),
            hasValidCookie: true,
          };
        } else {
          return rejectWithValue("Session verification failed");
        }
      } catch (error) {
        // Check if it's a 401 (cookie invalid/missing)
        if (error.response?.status === 401) {
          // Check if cookie still exists
          if (!hasAdminCookie()) {
            return rejectWithValue("Authentication cookies expired or missing");
          }

          // Try to refresh token
          try {
            const refreshResponse = await adminService.refreshToken();
            if (refreshResponse.success) {
              // Retry profile fetch
              const retryResponse = await adminService.getProfile();
              if (retryResponse.success) {
                const updatedUser = retryResponse.data?.admin || user;
                localStorage.setItem("admin_user", JSON.stringify(updatedUser));

                return {
                  user: updatedUser,
                  session: JSON.parse(
                    localStorage.getItem("admin_session") || "null",
                  ),
                  hasValidCookie: true,
                  wasRefreshed: true,
                };
              }
            }
          } catch (refreshError) {
            console.warn("Token refresh failed:", refreshError);
          }

          return rejectWithValue("Session expired - Please login again");
        }

        // For network errors, check if we have cookies
        if (
          error.code === "ERR_NETWORK" ||
          error.message.includes("Network") ||
          error.message.includes("timeout") ||
          error.message.includes("Failed to fetch")
        ) {
          // If we have cookies and user data, allow offline mode
          if (hasAdminCookie() && user) {
            return {
              user,
              session: JSON.parse(
                localStorage.getItem("admin_session") || "null",
              ),
              isOffline: true,
              hasValidCookie: hasAdminCookie(),
            };
          } else {
            return rejectWithValue("Network error and no valid session");
          }
        }

        // For other server errors
        return rejectWithValue("Server error, using cached session");
      }
    } catch (error) {
      return rejectWithValue(error.message || "Authentication failed");
    }
  },
);

// Async thunk for silent refresh
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.refreshToken();

      if (response.success) {
        // Verify the refreshed token works
        const profileResponse = await adminService.getProfile();

        if (profileResponse.success) {
          const updatedUser =
            profileResponse.data?.admin || getUserFromStorage();
          if (updatedUser) {
            localStorage.setItem("admin_user", JSON.stringify(updatedUser));
          }

          return {
            user: updatedUser,
            message: "Token refreshed successfully",
          };
        } else {
          throw new Error("Profile verification failed after refresh");
        }
      } else {
        throw new Error(response.message || "Token refresh failed");
      }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to refresh token");
    }
  },
);

// Update the auth slice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    isAdmin: false,
    isLoading: true,
    isAuthenticated: false,
    error: null,
    permissions: [],
    session: null,
    isOffline: false,
    lastActivity: Date.now(),
    hasCookies: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSession: (state, action) => {
      state.session = action.payload;
    },
    updateUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem("admin_user", JSON.stringify(state.user));
      }
    },
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
      // Update in localStorage
      if (typeof window !== "undefined") {
        window.lastActivityTime = Date.now();
      }
    },
    // Initialize from storage on app start
    initializeFromStorage: (state) => {
      const user = getUserFromStorage();
      const hasCookies = hasAdminCookie();

      if (user && hasCookies) {
        const sessionStr = localStorage.getItem("admin_session");
        let session = null;
        if (sessionStr && sessionStr !== "null") {
          try {
            session = JSON.parse(sessionStr);
          } catch (e) {
            console.error("Session parsing error:", e);
          }
        }

        state.user = user;
        state.isAdmin = user?.role === "admin" || user?.role === "super_admin";
        state.isAuthenticated = true; // Only true if we have both user AND cookies
        state.permissions = user?.permissions || [];
        state.session = session;
        state.isOffline = false;
        state.error = null;
        state.lastActivity = Date.now();
        state.hasCookies = true;
      } else if (user && !hasCookies) {
        // User data exists but no cookies - session expired
        state.user = user;
        state.isAdmin = user?.role === "admin" || user?.role === "super_admin";
        state.isAuthenticated = false; // Not authenticated without cookies
        state.permissions = user?.permissions || [];
        state.session = null;
        state.isOffline = false;
        state.error = "Session expired - Please login again";
        state.lastActivity = Date.now();
        state.hasCookies = false;
      } else {
        state.isAuthenticated = false;
        state.hasCookies = hasCookies;
        state.error = hasCookies ? "No user data found" : null;
      }
      state.isLoading = false;
    },

    // Force offline mode
    setOfflineMode: (state, action) => {
      state.isOffline = action.payload;
      if (action.payload) {
        state.error = "You are in offline mode. Some features may be limited.";
      }
    },

    // Clear auth state without clearing localStorage
    clearAuthState: (state) => {
      state.user = null;
      state.isAdmin = false;
      state.isAuthenticated = false;
      state.permissions = [];
      state.session = null;
      state.isOffline = false;
      state.error = null;
      state.lastActivity = Date.now();
      state.hasCookies = false;
    },

    // Check cookies status
    checkCookies: (state) => {
      state.hasCookies = hasAdminCookie();
      if (!state.hasCookies && state.isAuthenticated) {
        state.isAuthenticated = false;
        state.error = "Session expired - Cookies missing";
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.isAuthenticated = false;
      state.isOffline = false;
      state.hasCookies = false;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.isAdmin =
        action.payload.user?.role === "admin" ||
        action.payload.user?.role === "super_admin";
      state.permissions = action.payload.permissions || [];
      state.session = action.payload.session;
      state.error = null;
      state.isOffline = false;
      state.lastActivity = Date.now();
      state.hasCookies = hasAdminCookie(); // Check cookies after login
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.error = action.payload;
      state.isOffline = false;
      state.hasCookies = false;
    });

    // Logout
    builder.addCase(logout.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.isAdmin = false;
      state.permissions = [];
      state.session = null;
      state.error = null;
      state.isOffline = false;
      state.hasCookies = false;
    });
    builder.addCase(logout.rejected, (state) => {
      state.isLoading = false;
      // Still clear state even if logout API fails
      state.isAuthenticated = false;
      state.user = null;
      state.isAdmin = false;
      state.permissions = [];
      state.session = null;
      state.error = null;
      state.isOffline = false;
      state.hasCookies = false;
    });

    // Check Auth
    builder.addCase(checkAuth.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(checkAuth.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isOffline = action.payload.isOffline || false;
      state.hasCookies = action.payload.hasValidCookie || false;

      if (action.payload.user) {
        state.isAuthenticated = true; // Only authenticated if we have valid cookies
        state.user = action.payload.user;
        state.isAdmin =
          action.payload.user?.role === "admin" ||
          action.payload.user?.role === "super_admin";
        state.permissions = action.payload.user?.permissions || [];
        state.session = action.payload.session;
        state.error = action.payload.isOffline
          ? "Using cached session. Some features may be limited."
          : null;
        state.lastActivity = Date.now();
      } else {
        state.isAuthenticated = false;
        state.error = "Invalid authentication data";
        state.isOffline = false;
        state.hasCookies = false;
      }
    });
    builder.addCase(checkAuth.rejected, (state, action) => {
      state.isLoading = false;
      state.hasCookies = hasAdminCookie();

      const user = getUserFromStorage();

      if (user && state.hasCookies) {
        // We have user and cookies but server rejected - use cached with warning
        state.isAuthenticated = true;
        state.user = user;
        state.isAdmin = user?.role === "admin" || user?.role === "super_admin";
        state.permissions = user?.permissions || [];
        state.session = JSON.parse(
          localStorage.getItem("admin_session") || "null",
        );
        state.error = action.payload || "Using cached session";
        state.isOffline = true;
        state.lastActivity = Date.now();
      } else if (user && !state.hasCookies) {
        // User data exists but no cookies
        state.isAuthenticated = false;
        state.user = user;
        state.isAdmin = user?.role === "admin" || user?.role === "super_admin";
        state.permissions = user?.permissions || [];
        state.session = JSON.parse(
          localStorage.getItem("admin_session") || "null",
        );
        state.error = "Session expired - Please login again";
        state.isOffline = false;
      } else {
        // No user data
        state.isAuthenticated = false;
        state.user = null;
        state.isAdmin = false;
        state.permissions = [];
        state.session = null;
        state.error = action.payload;
        state.isOffline = false;
      }
    });

    // Refresh Token
    builder.addCase(refreshToken.fulfilled, (state, action) => {
      state.isLoading = false;
      if (action.payload.user) {
        state.user = action.payload.user;
        localStorage.setItem("admin_user", JSON.stringify(action.payload.user));
      }
      state.error = null;
      state.isOffline = false;
      state.lastActivity = Date.now();
      state.hasCookies = hasAdminCookie();
    });
    builder.addCase(refreshToken.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isOffline = true;
      state.hasCookies = hasAdminCookie();
    });
  },
});

export const {
  clearError,
  setSession,
  updateUser,
  initializeFromStorage,
  setOfflineMode,
  clearAuthState,
  updateLastActivity,
  checkCookies,
} = authSlice.actions;

export default authSlice.reducer;
